// DispatchEntry.jsx
"use client";

import { useState, useEffect } from "react";
import {
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePrinter,
  AiOutlineReload,
} from "react-icons/ai";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/CONSTANTS";
import Header from "../components/header";

export default function DispatchEntry() {
  const navigate = useNavigate();

  // page state
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(false);

  // single entry form
  const [formData, setFormData] = useState({
    vno: "",
    Date: "",
    partyName: "",
    cardNo: "",
    description: "",
    qty: "",
    weight: "",
    remark: "",
    totalwt: "",
    isInvoice: false,
    invLabel: "", // current invoice label for this vno (if exists)
  });
  const [formError, setFormError] = useState({});

  // next invoice preview label & number (global)
  const [nextInvLabel, setNextInvLabel] = useState("");
  const [nextInvNo, setNextInvNo] = useState(null); // numeric

  // lookups
  const [allVNumbers, setAllVNumbers] = useState([]);
  const [allparty, setparty] = useState([]);
  const [allCardNumbers, setAllCardNumbers] = useState([]);
  const [availableqty, setAvailableQTY] = useState(0);

  // estimate cache (cardNo -> gramsPerPiece) for instant weight calc
  const [estimateMap, setEstimateMap] = useState({});

  // Bulk entry (packing -> create dispatchs)
  const [showBulkEntry, setShowBulkEntry] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [rowState, setRowState] = useState({}); // per-row state
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    pageSize: 15,
    sortBy: "cardNo",
    sortDir: "asc",
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);

  // Saved dispatches (Show All / Bulk Print)
  const [showSavedDispatches, setShowSavedDispatches] = useState(false);
  const [savedRows, setSavedRows] = useState([]);
  const [savedRowState, setSavedRowState] = useState({});
  const [loadingSaved, setLoadingSaved] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  // small validator
  const validateField = (name, value) => {
    if (!value && value !== 0 && value !== false) return `${name} is required.`;
    return "";
  };

  // ---------- initial ----------
  useEffect(() => {
    fetchAllVNumbers();
    fetchPartynames();
    fetchNextInvoiceLabel(); // preview invoice from start
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch v numbers
  const fetchAllVNumbers = async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/dispatch/getvno`, { withCredentials: true });
      const nums = (resp.data.vnum || []).map((x) => x.vno).sort((a, b) => Number(b) - Number(a));
      setAllVNumbers(nums);
      return nums;
    } catch (err) {
      console.error("fetchAllVNumbers", err);
      setAllVNumbers([]);
      return [];
    }
  };

  // party names
  const fetchPartynames = async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/order/getPartynames`, { withCredentials: true });
      const result = Array.isArray(resp.data.cardNos) ? resp.data.cardNos : resp.data;
      setparty(result);
    } catch (err) {
      console.error("fetchPartynames", err);
    }
  };

  // next invoice preview (fetch numeric & label)
  const fetchNextInvoiceLabel = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/dispatch/nextinv`, { withCredentials: true });
      const data = res.data || {};
      if (data && data.label) {
        setNextInvLabel(data.label);
        setNextInvNo(Number(data.invNo ?? (data.invNo === 0 ? data.invNo : null)));
      } else {
        setNextInvLabel("");
        setNextInvNo(null);
      }
    } catch (err) {
      console.error("fetchNextInvoiceLabel error", err);
      setNextInvLabel("");
      setNextInvNo(null);
    }
  };

  // when single-entry party changes fetch card numbers
  useEffect(() => {
    if (formData.partyName && formData.partyName.trim() !== "") {
      fetchAllCardNumbersForParty(formData.partyName);
    } else {
      setAllCardNumbers([]);
    }
  }, [formData.partyName]);

  const fetchAllCardNumbersForParty = async (party) => {
    try {
      if (!party) return setAllCardNumbers([]);
      const orderResp = await fetch(`${API_BASE_URL}/api/order/getCardno/${encodeURIComponent(party)}`);
      if (!orderResp.ok) {
        setAllCardNumbers([]);
        return;
      }
      const json = await orderResp.json();
      const cardNos = Array.isArray(json.cardNos) ? json.cardNos : [];
      // normalize to objects { cardNo: "..." }
      const normalized = cardNos.map((it) =>
        (typeof it === "object" ? { cardNo: String(it.cardNo ?? it.cardno) } : { cardNo: String(it) })
      );

      // Helper: try to extract a numeric value from cardNo (first number sequence)
      const extractNumber = (s) => {
        if (s == null) return null;
        const m = String(s).match(/-?\d+(\.\d+)?/);
        if (m) return Number(m[0]);
        const n = Number(String(s).trim());
        return Number.isFinite(n) ? n : null;
      };

      // sort descending: prefer numeric comparison when possible, otherwise string compare
      normalized.sort((a, b) => {
        const na = extractNumber(a.cardNo);
        const nb = extractNumber(b.cardNo);
        if (na !== null && nb !== null) {
          return nb - na; // descending numeric
        }
        // fallback to string comparison (descending)
        return String(b.cardNo).localeCompare(String(a.cardNo));
      });

      setAllCardNumbers(normalized);
    } catch (err) {
      console.error("fetchAllCardNumbersForParty", err);
      setAllCardNumbers([]);
    }
  };

  // single available qty (packages - dispatched)
  useEffect(() => {
    const cardno = formData.cardNo;
    if (!cardno) {
      setAvailableQTY(0);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/package/getPackages/${encodeURIComponent(cardno)}`);
        let pkgqty = 0;
        const packingdata = res.data.data || [];
        for (const pkg of packingdata) pkgqty += Number(pkg.totalquantity || 0);

        const res2 = await axios.get(`${API_BASE_URL}/api/dispatch/getbycardno/${encodeURIComponent(cardno)}`);
        const dispatchdata = res2.data.cardData || [];
        for (const d of dispatchdata) pkgqty -= Number(d.qty || 0);

        setAvailableQTY(pkgqty);
      } catch (err) {
        console.error("available qty error", err);
        setAvailableQTY(0);
      }
    })();
  }, [formData.cardNo]);

  // single weight calc (uses order estimateWt & estimateQty -> grams per piece)
  useEffect(() => {
    const cardno = formData.cardNo;
    const qty = Number(formData.qty || 0);
    if (!cardno || !qty) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardno)}`,
          { withCredentials: true }
        );
        const carddata = res.data.cardData || {};
        const estimateWt = Number(carddata.estimateWt ?? carddata.estimatewt ?? 0);    // grams for estimateQty pcs
        const estimateQty = Number(carddata.estimateQty ?? carddata.estimateqty ?? 0); // pcs

        let wtKg = 0;
        if (estimateWt > 0 && estimateQty > 0 && qty > 0) {
          const perPieceGm = estimateWt / estimateQty; // g per pc
          wtKg = (qty * perPieceGm) / 1000;           // kg
        }

        setFormData((prev) => ({ ...prev, weight: wtKg }));
      } catch (err) {
        console.error("single weight calc error", err);
      }
    })();
  }, [formData.cardNo, formData.qty]);

  // description extractor (robust)
  const getDescriptionFromRaw = (raw) => {
    if (!raw || typeof raw !== "object") return "";
    const keys = [
      "drgNo", "drgno", "DrgNo", "Drgno",
      "description", "Description", "desc",
      "drg", "drg_no",
      "drgName", "drgname"
    ];
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(raw, k) && raw[k] != null && String(raw[k]).trim() !== "") {
        return String(raw[k]);
      }
    }
    for (const k of Object.keys(raw)) {
      if (/drg/i.test(k) || /desc/i.test(k) || /description/i.test(k)) {
        const v = raw[k];
        if (v != null && String(v).trim() !== "") return String(v);
      }
    }
    return "";
  };

  // ---------- Bulk Entry (packing list) ----------
  useEffect(() => {
    // load table automatically when bulk-entry open or date filters change
    if (showBulkEntry) loadTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.sortBy, filters.sortDir, filters.pageSize, showBulkEntry]);

  const loadTable = async () => {
    setLoadingTable(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/report/packingall`, {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          pageSize: filters.pageSize,
          sortBy: filters.sortBy,
          sortDir: filters.sortDir,
        },
      });

      const packages = resp.data.packages || resp.data.data || [];
      const total = resp.data.totalCount ?? resp.data.total ?? packages.length;

      const normalized = packages.map((p, idx) => {
        const id = `${p.cardNo}-${idx}`;
        const descFromRaw = getDescriptionFromRaw(p);
        return {
          id,
          partySpec: p.partyName || p.partySpec || p.packerName || p.party || "",
          cardNo: String(p.cardNo ?? ""),
          drgNo: descFromRaw || p.drgNo || p.description || "",
          packQty: Number(p.totalquantity ?? p.packQty ?? 0),
          packingDate: p.date ?? p.packingDate ?? p.createdAt ?? "",
          raw: p,
          availableQty: null,
        };
      });

      // fetch per-piece weights (grams per piece) for unique cardNos
      const uniqueCardNos = Array.from(new Set(normalized.map((r) => r.cardNo).filter(Boolean)));
      const estimatePromises = uniqueCardNos.map(async (cardNo) => {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardNo)}`,
            { withCredentials: true }
          );
          const carddata = res.data.cardData || {};
          const estimateWt = Number(carddata.estimateWt ?? carddata.estimatewt ?? 0);    // g
          const estimateQty = Number(carddata.estimateQty ?? carddata.estimateqty ?? 0); // pcs

          let perPieceGm = 0;
          if (estimateWt > 0 && estimateQty > 0) {
            perPieceGm = estimateWt / estimateQty; // g per pc
          }
          return { cardNo, perPieceGm };
        } catch (err) {
          console.error("estimate lookup error for", cardNo, err);
          return { cardNo, perPieceGm: 0 };
        }
      });
      const estimateResults = await Promise.all(estimatePromises);
      const localEstimateMap = {};
      estimateResults.forEach((r) => { localEstimateMap[r.cardNo] = r.perPieceGm ?? 0; });
      // store to state for later instant use
      setEstimateMap(localEstimateMap);

      // compute available qty per row in parallel
      const availPromises = normalized.map(async (r) => {
        try {
          const pk = await axios.get(`${API_BASE_URL}/api/package/getPackages/${encodeURIComponent(r.cardNo)}`);
          const packingdata = pk.data.data || [];
          let pkgqty = 0;
          for (const pkg of packingdata) pkgqty += Number(pkg.totalquantity || 0);

          const dresp = await axios.get(`${API_BASE_URL}/api/dispatch/getbycardno/${encodeURIComponent(r.cardNo)}`);
          const dispatchdata = dresp.data.cardData || [];
          for (const d of dispatchdata) pkgqty -= Number(d.qty || 0);

          return { id: r.id, availableQty: pkgqty };
        } catch (err) {
          console.error("compute available error for", r.cardNo, err);
          return { id: r.id, availableQty: 0 };
        }
      });

      const avails = await Promise.all(availPromises);
      const availMap = {};
      for (const a of avails) availMap[a.id] = a.availableQty;

      // init rowState with dispatchQty auto-filled from packQty and dispatchWeight computed from cached per-piece g
      const init = {};
      normalized.forEach((r) => {
        r.availableQty = availMap[r.id] ?? 0;

        // prefill dispatchQty from packQty
        const preQty = r.packQty != null ? String(r.packQty) : "";

        // compute weight synchronously from cached grams-per-piece if available
        const perPieceGm = Number(localEstimateMap[r.cardNo] ?? 0);
        const preWt =
          perPieceGm && preQty
            ? (Number(preQty) * perPieceGm) / 1000
            : "";

        init[r.id] = {
          selected: false,
          dispatchQty: preQty,
          dispatchDate: today,
          dispatchWeight: preWt === 0 ? (preWt === 0 ? "0" : "") : preWt,
          remark: "",
          isInvoice: false,
        };
      });

      setTableRows(normalized);
      setTotalCount(Number(total) || normalized.length);
      setRowState(init);
    } catch (err) {
      console.error("loadTable error", err);
      toast.error("Failed to load packing entries", { autoClose: 5000 });
    } finally {
      setLoadingTable(false);
    }
  };

  // compute per-row weight when dispatchQty changes (fallback when estimateMap doesn't have entry)
  useEffect(() => {
    const ids = Object.keys(rowState).filter((id) => rowState[id]?.dispatchQty);
    if (ids.length === 0) return;
    ids.forEach(async (id) => {
      try {
        const r = tableRows.find((t) => t.id === id);
        if (!r) return;
        const cardNo = r.cardNo;
        const qty = Number(rowState[id].dispatchQty || 0);
        if (!cardNo || !qty) {
          // if qty becomes empty/zero, clear weight
          setRowState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), dispatchWeight: "" } }));
          return;
        }

        // if we already have grams-per-piece cached, don't do extra call
        const cachedPerPieceGm = estimateMap[cardNo];
        if (cachedPerPieceGm !== undefined) {
          const perPieceGm = Number(cachedPerPieceGm || 0);
          const wtKg = (qty * perPieceGm) / 1000 || 0;
          setRowState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), dispatchWeight: wtKg } }));
          return;
        }

        // fallback to fetch estimate & compute per-piece
        const res = await axios.get(`${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardNo)}`);
        const carddata = res.data.cardData || {};
        const estimateWt = Number(carddata.estimateWt ?? carddata.estimatewt ?? 0);
        const estimateQty = Number(carddata.estimateQty ?? carddata.estimateqty ?? 0);

        let perPieceGm = 0;
        if (estimateWt > 0 && estimateQty > 0) {
          perPieceGm = estimateWt / estimateQty;
        }

        // store grams-per-piece in cache for future
        setEstimateMap((prev) => ({ ...prev, [cardNo]: perPieceGm }));

        const wtKg = (qty * perPieceGm) / 1000 || 0;
        setRowState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), dispatchWeight: wtKg } }));
      } catch (err) {
        console.error("per-row weight calc error", err);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowState, tableRows]);

  // helper to update row state
  const handleRowChange = (id, field, value) => {
    setRowState((prev) => {
      const next = { ...prev };
      next[id] = { ...(next[id] || {}) };

      if (field === "selected") {
        next[id].selected = value;
        if (value && (!next[id].dispatchDate || String(next[id].dispatchDate).trim() === "")) {
          next[id].dispatchDate = today;
        }
        return next;
      }

      next[id][field] = value;

      // If admin changed dispatchQty, compute dispatchWeight immediately if we have grams-per-piece cached
      if (field === "dispatchQty") {
        const row = tableRows.find((t) => t.id === id);
        const cardNo = row?.cardNo;
        const qtyNum = Number(value || 0);
        const perPieceGm = Number(estimateMap[cardNo] ?? null);

        if (Number.isFinite(perPieceGm) && perPieceGm > 0 && qtyNum) {
          const wtKg = (qtyNum * perPieceGm) / 1000;
          next[id].dispatchWeight = wtKg;
        } else if (qtyNum === 0 || !qtyNum) {
          next[id].dispatchWeight = "";
        } else {
          // no cached estimate: leave weight blank for fallback effect to fetch
          next[id].dispatchWeight = "";
        }
      }

      return next;
    });
  };

  // Helper: compute predicted invoice label for a given row based on nextInvNo & current isInvoice flags.
  // Strategy: iterate rows in displayed order; for each row, count how many earlier rows are flagged isInvoice (rowState[...].isInvoice === true).
  // Predicted number for this row = nextInvNo + countCheckedBefore
  const getPredictedLabelForRow = (rowIndex, rowId) => {
    if (!Number.isFinite(nextInvNo)) return "---";
    // count how many rows BEFORE this index are flagged so they consume earlier inv numbers
    let countBefore = 0;
    for (let i = 0; i < rowIndex; i++) {
      const rid = tableRows[i]?.id;
      if (!rid) continue;
      const st = rowState[rid];
      if (st?.isInvoice) countBefore++;
    }
    // predicted number if this row is ticked (given current selections before it)
    const predNum = nextInvNo + countBefore;
    return `INV-${predNum}`;
  };

  // toggle bulk entry view
  const toggleBulkEntry = () => {
    const newVal = !showBulkEntry;
    setShowBulkEntry(newVal);
    if (newVal) {
      setShowSavedDispatches(false);
    }
  };

  // toggle saved dispatches view
  const toggleSavedDispatches = () => {
    const newVal = !showSavedDispatches;
    setShowSavedDispatches(newVal);
    if (newVal) {
      setShowBulkEntry(false);
      loadSavedDispatches();
    }
  };

  const toggleSort = (key) => {
    setFilters((p) => {
      const dir = p.sortBy === key && p.sortDir === "asc" ? "desc" : "asc";
      return { ...p, sortBy: key, sortDir: dir };
    });
  };

  // ---------- Bulk Save (Save Selected from packing rows) ----------
  const handleSaveSelected = async () => {
    const selectedRows = tableRows.filter((r) => rowState[r.id]?.selected);
    if (selectedRows.length === 0) {
      toast.info("Please select entries first", { autoClose: 5000 });
      return;
    }

    const items = [];
    const errors = [];
    const cardNosToLookup = new Set();

    // When saving bulk with isInvoice flags, the backend will create invoices in the order of assigned vnos.
    for (const r of selectedRows) {
      const st = rowState[r.id] || {};
      const qty = st.dispatchQty;
      const date = st.dispatchDate;
      let wt = st.dispatchWeight;
      const remark = st.remark || "";
      const isInvoice = st.isInvoice === true;

      if (!qty && qty !== 0) {
        errors.push({ id: r.id, reason: "Dispatch Qty required" });
        continue;
      }
      if (!date) {
        errors.push({ id: r.id, reason: "Dispatch Date required" });
        continue;
      }

      const description = r.drgNo || getDescriptionFromRaw(r.raw) || (r.raw && (r.raw.description || r.raw.drgNo)) || "";
      if (!description || String(description).trim() === "") {
        errors.push({ id: r.id, reason: "Description required" });
        continue;
      }

      if (wt === "" || wt === null || wt === undefined) cardNosToLookup.add(r.cardNo);

      items.push({
        __rowId: r.id,
        Date: date,
        partyName: r.partySpec || r.raw?.partyName || "",
        cardNo: r.cardNo,
        description,
        qty: Number(qty),
        weight: wt === "" || wt === null || wt === undefined ? null : Number(wt),
        remark,
        isInvoice,
      });
    }

    if (errors.length > 0) {
      toast.error(`Fix ${errors.length} rows before saving — see console for details`, { autoClose: 5000 });
      console.error("validation errors", errors);
      return;
    }

    const toastId = toast.loading("Saving selected dispatch entries...");

    try {
      if (cardNosToLookup.size > 0) {
        const lookups = Array.from(cardNosToLookup).map(async (cardNo) => {
          try {
            const res = await axios.get(`${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardNo)}`);
            const carddata = res.data.cardData || {};
            const estimateWt = Number(carddata.estimateWt ?? carddata.estimatewt ?? 0);
            const estimateQty = Number(carddata.estimateQty ?? carddata.estimateqty ?? 0);

            let perPieceGm = 0;
            if (estimateWt > 0 && estimateQty > 0) {
              perPieceGm = estimateWt / estimateQty;
            }
            return { cardNo, perPieceGm };
          } catch (err) {
            console.error("estimate lookup error for", cardNo, err);
            return { cardNo, perPieceGm: 0 };
          }
        });
        const lookupResults = await Promise.all(lookups);
        const estimateMapLocal = {};
        lookupResults.forEach((r) => { estimateMapLocal[r.cardNo] = r.perPieceGm ?? 0; });

        for (const it of items) {
          if (it.weight === null) {
            const perPieceGm = Number(estimateMapLocal[it.cardNo] ?? 0);
            it.weight = (it.qty * perPieceGm) / 1000 || 0;
          }
        }
      }

      const resp = await axios.post(`${API_BASE_URL}/api/dispatch/createBulk`, { items }, { withCredentials: true });
      const data = resp.data || {};
      const inserted = data.insertedCount ?? (Array.isArray(items) ? items.length : 0);

      await fetchAllVNumbers();
      setShowBulkEntry(false);

      toast.update(toastId, { render: `Bulk dispatch saved (${inserted})`, type: "success", isLoading: false, autoClose: 5000 });
      if (data.skipped && data.skipped.length) {
        console.warn("Skipped items:", data.skipped);
        toast.warn(`Some items were skipped (${data.skipped.length}) — see console.`, { autoClose: 5000 });
      }
      if (data.invoicesCreatedCount) {
        toast.success(`${data.invoicesCreatedCount} invoices created`, { autoClose: 5000 });
      }

      // refresh invoice preview after save
      fetchNextInvoiceLabel();
    } catch (err) {
      console.error("createBulk error", err);
      const msg = axios.isAxiosError(err) ? (err.response?.data?.message || err.message) : "Save selected failed (see server)";
      toast.update(toastId, { render: msg, type: "error", isLoading: false, autoClose: 5000 });
    }
  };

  // ---------- Saved Dispatches (Show All / Bulk Print) ----------
  useEffect(() => {
    if (showSavedDispatches) loadSavedDispatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, showSavedDispatches]);

  const loadSavedDispatches = async () => {
    setLoadingSaved(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/dispatch/getall`, {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
        withCredentials: true,
      });

      let rows = resp.data.data || resp.data || [];
      if (!Array.isArray(rows)) rows = [];

      rows.sort((a, b) => {
        const av = Number(a.vno ?? 0);
        const bv = Number(b.vno ?? 0);
        return bv - av;
      });

      const norm = rows.map((d, idx) => {
        const id = String(d._id ?? `${d.vno}-${idx}` ?? idx);
        return {
          id,
          vno: d.vno,
          Date: d.Date,
          partyName: d.partyName,
          cardNo: String(d.cardNo ?? ""),
          description: d.description ?? d.drgNo ?? "",
          qty: d.qty,
          weight: d.weight,
          remark: d.remark || "",
          raw: d,
          isInvoice: d.invoice?.isInvoice === true,
          invLabel: d.invoice?.invLabel || "",
          invNo: d.invoice?.invNo ?? null,
        };
      });

      setSavedRows(norm);
      const init = {};
      norm.forEach((r) => {
        init[r.id] = { selected: false };
      });
      setSavedRowState(init);
    } catch (err) {
      console.error("loadSavedDispatches error", err);
      toast.error("failed to load saved dispatch entries", { autoClose: 5000 });
      setSavedRows([]);
      setSavedRowState({});
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleSavedToggle = (id, checked) => {
    setSavedRowState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), selected: checked } }));
  };

  const handlePrintSelectedSaved = () => {
    const selected = savedRows.filter((r) => savedRowState[r.id]?.selected);
    if (selected.length === 0) {
      toast.info("Please select saved entries to print", { autoClose: 5000 });
      return;
    }
    navigate("/dispatchpdf", { state: { bulk: selected } });
  };

  // ---------- Single entry handlers ----------
  const fetchDataByVNo = async (vno) => {
    if (!vno) return resetForm();
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/dispatch/get/${encodeURIComponent(vno)}`, { withCredentials: true });
      const cardData = resp.data.cardData || {};
      const formattedDate = cardData.Date ? new Date(cardData.Date).toISOString().split("T")[0] : "";
      var { weight, ...userdata } = cardData;
      userdata.Date = formattedDate;

      if (cardData.invoice) {
        userdata.isInvoice = cardData.invoice.isInvoice === true;
        userdata.invLabel = cardData.invoice.invLabel || "";
      } else {
        userdata.isInvoice = false;
        userdata.invLabel = "";
      }

      setFormData(userdata);
      setIsFormEnabled(true);
      setIsNewEntry(false);

      // after loading existing, keep preview updated too
      fetchNextInvoiceLabel();
    } catch (err) {
      console.error("fetchDataByVNo error", err);
    }
  };

  // fetch description for single entry when cardNo changes
  useEffect(() => {
    const cardno = formData.cardNo;
    if (!cardno) return;
    let mounted = true;
    (async () => {
      try {
        try {
          const resp = await axios.get(`${API_BASE_URL}/api/order/getDrgByCardNo/${encodeURIComponent(cardno)}`, { withCredentials: true });
          if (mounted && resp?.data?.drgNo) {
            setFormData((p) => ({ ...p, description: resp.data.drgNo }));
            return;
          }
        } catch (e) { /* fallback */ }
        const resp2 = await axios.get(`${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardno)}`, { withCredentials: true });
        const cd = resp2.data.cardData || {};
        const desc = cd.drgNo || cd.description || cd.drgno || cd.DrgNo || cd.drg || "";
        if (mounted && desc) setFormData((p) => ({ ...p, description: desc }));
      } catch (err) {
        console.error("single description fetch error", err);
      }
    })();
    return () => { mounted = false; };
  }, [formData.cardNo]);

  const resetForm = () => {
    setFormData({
      vno: "",
      Date: "",
      partyName: "",
      cardNo: "",
      description: "",
      qty: "",
      weight: "",
      remark: "",
      totalwt: "",
      isInvoice: false,
      invLabel: "",
    });
    setFormError({});
    setIsFormEnabled(false);
    setIsNewEntry(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((p) => ({ ...p, [name]: checked }));
      setFormError((p) => ({ ...p, [name]: "" }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
      setFormError((p) => ({ ...p, [name]: validateField(name, value) }));
    }
  };

  const handleAddSingle = async () => {
    if (isNewEntry) {
      const errors = {
        vno: validateField("vno", formData.vno),
        Date: validateField("Date", formData.Date),
        partyName: validateField("partyName", formData.partyName),
        cardNo: validateField("cardNo", formData.cardNo),
        description: validateField("description", formData.description),
        qty: validateField("qty", formData.qty),
        weight: validateField("weight", formData.weight),
      };
      setFormError(errors);
      if (Object.values(errors).some((x) => x)) return;
      try {
        const resp = await axios.post(`${API_BASE_URL}/api/dispatch/createDispatch`, formData, { withCredentials: true });
        const data = resp.data || {};
        if (data.invoice) {
          toast.success(`Dispatch created — ${data.invoice.label}`, { autoClose: 5000 });
        } else {
          toast.success("Dispatch created", { autoClose: 5000 });
        }

        await fetchAllVNumbers();
        resetForm();           // <- no auto load/lock
        fetchNextInvoiceLabel(); // refresh preview
      } catch (err) {
        console.error("createDispatch error", err);
        toast.error(err.response?.data?.message || "Create dispatch failed", { autoClose: 5000 });
      }
    } else {
      setIsNewEntry(true);
      setIsFormEnabled(true);
      const nums = allVNumbers.map((n) => Number(n)).filter((n) => !isNaN(n));
      const maxvalue = nums.length > 0 ? Math.max(...nums) : 0;
      setFormData((p) => ({
        ...p,
        vno: maxvalue + 1,
        Date: today,
        isInvoice: false,
        invLabel: "",
      }));
    }
  };

  const handleUpdateSingle = async () => {
    if (!isFormEnabled || isNewEntry) return;
    const errors = {
      vno: validateField("vno", formData.vno),
      Date: validateField("Date", formData.Date),
      partyName: validateField("partyName", formData.partyName),
      cardNo: validateField("cardNo", formData.cardNo),
      description: validateField("description", formData.description),
      qty: validateField("qty", formData.qty),
      weight: validateField("weight", formData.weight),
    };
    setFormError(errors);
    if (Object.values(errors).some((x) => x)) return;
    try {
      const resp = await axios.put(`${API_BASE_URL}/api/dispatch/updatedDispatch/${encodeURIComponent(formData.vno)}`, formData, { withCredentials: true });
      const data = resp.data || {};
      if (data.invoice) {
        toast.success(`Updated — ${data.invoice.invLabel || ""}`, { autoClose: 5000 });
      } else {
        toast.success("Updated", { autoClose: 5000 });
      }
      await fetchAllVNumbers();
      resetForm();
      fetchNextInvoiceLabel();
    } catch (err) {
      console.error("update error", err);
      toast.error("Update failed", { autoClose: 5000 });
    }
  };

  const handleDeleteSingle = async () => {
    if (!isFormEnabled || isNewEntry) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/dispatch/deleteDispatch/${encodeURIComponent(formData.vno)}`, { withCredentials: true });
      toast.success("Deleted", { autoClose: 5000 });
      await fetchAllVNumbers();
      resetForm();
      fetchNextInvoiceLabel();
    } catch (err) {
      console.error("delete error", err);
      toast.error("Delete failed", { autoClose: 5000 });
    }
  };

  // UI render
  return (
    <>
      <Header />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnHover draggable pauseOnFocusLoss />
      <div className="p-4 flex items-center justify-center">
        <div className={`bg-white shadow-xl rounded-lg ${isMaximized ? "w-full h-full" : "w-[1100px]"}`}>
          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-[#1d5e5c] to-[#126f6c] text-white rounded-t-lg">
            <span className="text-sm font-medium">Dispatch Entry</span>
            <div className="flex items-center gap-2">
              <button onClick={toggleBulkEntry} className="px-2 py-1 bg-white text-black rounded text-sm">
                {showBulkEntry ? "Back to Entry" : "Bulk Entry"}
              </button>
              <button onClick={toggleSavedDispatches} className="px-2 py-1 bg-white text-black rounded text-sm">
                {showSavedDispatches ? "Back to Entry" : "Show All"}
              </button>
              <button onClick={() => setIsMaximized(!isMaximized)} className="px-2 py-1 bg-white text-black rounded text-sm">
                □
              </button>
            </div>
          </div>

          <div className="p-4">
            {showBulkEntry ? (
              // ===================== BULK ENTRY VIEW =====================
              <div className="bg-[#E6FFFF] p-3 rounded space-y-3">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm">Start</label>
                    <input type="date" value={filters.startDate} onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))} className="border rounded px-1 py-0.5 text-sm" />
                    <label className="text-sm">End</label>
                    <input type="date" value={filters.endDate} onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))} className="border rounded px-1 py-0.5 text-sm" />
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-700">{loadingTable ? "Loading..." : `${totalCount} records`}</div>
                    <div className="text-sm text-gray-700 px-2 py-0.5 bg-yellow-50 rounded border">{nextInvLabel || "---"}</div>
                    <button onClick={handleSaveSelected} className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Save Selected</button>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[60vh] border rounded">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 sticky top-0">
                        <th className="p-2 border w-12 text-center">Select</th>
                        <th onClick={() => toggleSort("cardNo")} className="p-2 border cursor-pointer">Card No {filters.sortBy === "cardNo" ? (filters.sortDir === "asc" ? "▲" : "▼") : ""}</th>
                        <th onClick={() => toggleSort("packingDate")} className="p-2 border cursor-pointer">Packing Date {filters.sortBy === "packingDate" ? (filters.sortDir === "asc" ? "▲" : "▼") : ""}</th>
                        <th onClick={() => toggleSort("partySpec")} className="p-2 border cursor-pointer">Party {filters.sortBy === "partySpec" ? (filters.sortDir === "asc" ? "▲" : "▼") : ""}</th>
                        <th className="p-2 border">Drg. No.</th>
                        <th className="p-2 border">Pack Qty</th>
                        <th className="p-2 border">Available</th>
                        <th className="p-2 border">Dispatch Qty</th>
                        <th className="p-2 border">Dispatch Date</th>
                        <th className="p-2 border">Wt (Kg)</th>
                        <th className="p-2 border">Is Inv</th>
                        <th className="p-2 border">If Inv →</th>
                        <th className="p-2 border">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((r, idx) => {
                        const st = rowState[r.id] || {};
                        const predicted = getPredictedLabelForRow(idx, r.id);
                        return (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="p-2 border text-center">
                              <input type="checkbox" checked={!!st.selected} onChange={(e) => handleRowChange(r.id, "selected", e.target.checked)} />
                            </td>
                            <td className="p-2 border">{r.cardNo}</td>
                            <td className="p-2 border">{r.packingDate ? new Date(r.packingDate).toISOString().split("T")[0] : ""}</td>
                            <td className="p-2 border">{r.partySpec}</td>
                            <td className="p-2 border">{r.drgNo}</td>
                            <td className="p-2 border">{r.packQty}</td>
                            <td className="p-2 border">{r.availableQty ?? ""}</td>

                            <td className="p-2 border">
                              <input type="number" className="w-24 border rounded px-1 py-0.5 text-sm" value={st.dispatchQty || ""} onChange={(e) => handleRowChange(r.id, "dispatchQty", e.target.value)} />
                            </td>

                            <td className="p-2 border">
                              <input type="date" className="w-36 border rounded px-1 py-0.5 text-sm" value={st.dispatchDate || ""} onChange={(e) => handleRowChange(r.id, "dispatchDate", e.target.value)} />
                            </td>

                            <td className="p-2 border">
                              <input type="text" className="w-28 border rounded px-1 py-0.5 text-sm" value={st.dispatchWeight || ""} readOnly />
                            </td>

                            <td className="p-2 border text-center">
                              <input type="checkbox" checked={!!st.isInvoice} onChange={(e) => handleRowChange(r.id, "isInvoice", e.target.checked)} />
                            </td>

                            <td className="p-2 border text-center">
                              <div className="text-xs px-2 py-0.5 rounded">
                                {predicted}
                              </div>
                            </td>

                            <td className="p-2 border">
                              <input type="text" className="w-36 border rounded px-1 py-0.5 text-sm" value={st.remark || ""} onChange={(e) => handleRowChange(r.id, "remark", e.target.value)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="text-sm text-gray-600">{totalCount} total records</div>
                </div>
              </div>
            ) : showSavedDispatches ? (
              // ===================== SAVED DISPATCHES VIEW =====================
              <div className="bg-[#E6FFFF] p-3 rounded space-y-3">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm">Start</label>
                    <input type="date" value={filters.startDate} onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))} className="border rounded px-1 py-0.5 text-sm" />
                    <label className="text-sm">End</label>
                    <input type="date" value={filters.endDate} onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))} className="border rounded px-1 py-0.5 text-sm" />
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-700">{loadingSaved ? "Loading..." : `${savedRows.length} records`}</div>
                    <button onClick={handlePrintSelectedSaved} className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Print Selected</button>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[60vh] border rounded">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 sticky top-0">
                        <th className="p-2 border w-12 text-center">Select</th>
                        <th className="p-2 border">V. No</th>
                        <th className="p-2 border">Inv</th>
                        <th className="p-2 border">Date</th>
                        <th className="p-2 border">Card No</th>
                        <th className="p-2 border">Party</th>
                        <th className="p-2 border">Description</th>
                        <th className="p-2 border">Qty</th>
                        <th className="p-2 border">Wt (Kg)</th>
                        <th className="p-2 border">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedRows.map((r) => {
                        const st = savedRowState[r.id] || {};
                        return (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="p-2 border text-center">
                              <input type="checkbox" checked={!!st.selected} onChange={(e) => handleSavedToggle(r.id, e.target.checked)} />
                            </td>
                            <td className="p-2 border">{r.vno}</td>
                            <td className="p-2 border">{r.isInvoice ? r.invLabel : ""}</td>
                            <td className="p-2 border">{r.Date ? new Date(r.Date).toISOString().split("T")[0] : ""}</td>
                            <td className="p-2 border">{r.cardNo}</td>
                            <td className="p-2 border">{r.partyName}</td>
                            <td className="p-2 border">{r.description}</td>
                            <td className="p-2 border">{r.qty}</td>
                            <td className="p-2 border">{r.weight}</td>
                            <td className="p-2 border">{r.remark}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="text-sm text-gray-600">{savedRows.length} total records</div>
                </div>
              </div>
            ) : (
              // ===================== SINGLE ENTRY VIEW =====================
              <div className="bg-[#E6FFFF] p-3 rounded space-y-4">
                {/* Top bar: VNo + actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">V. No.</label>
                    <select
                      name="vno"
                      value={formData.vno}
                      onChange={(e) => {
                        handleInputChange(e);
                        fetchDataByVNo(e.target.value);
                      }}
                      className="w-32 px-2 py-1 border rounded text-sm"
                      disabled={isNewEntry}
                    >
                      <option value="">Select V. No.</option>
                      {allVNumbers.map((vno) => (
                        <option key={vno} value={vno}>
                          {vno}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Form grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm block mb-1">Date</label>
                      <input
                        type="date"
                        name="Date"
                        value={formData.Date}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                        disabled={!isFormEnabled && !isNewEntry}
                      />
                    </div>

                    {/* Is Invoice ABOVE party name */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            name="isInvoice"
                            checked={!!formData.isInvoice}
                            onChange={(e) => handleInputChange(e)}
                            disabled={!isFormEnabled && !isNewEntry}
                          />
                          <span>Is Invoice</span>
                        </label>

                        <div className="text-xs px-2 py-1 bg-yellow-100 rounded border border-yellow-300 flex items-center gap-1">
                          <span className="font-semibold">Next:</span>
                          <span>{nextInvLabel || "---"}</span>
                        </div>

                        {formData.invLabel && (
                          <div className="text-xs px-2 py-1 bg-green-100 rounded border border-green-300 flex items-center gap-1">
                            <span className="font-semibold">Current:</span>
                            <span>{formData.invLabel}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Party Name</label>
                      <select
                        name="partyName"
                        value={formData.partyName}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Select Party Name</option>
                        {allparty.map((value, index) => (
                          <option key={index} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Card No.</label>
                      <select
                        name="cardNo"
                        value={formData.cardNo}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Select Card No.</option>
                        {allCardNumbers.map((tool) => (
                          <option key={tool.cardNo} value={tool.cardNo}>
                            {tool.cardNo}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Description</label>
                      <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                        disabled={!isFormEnabled && !isNewEntry}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-sm block mb-1">Available</label>
                      <div className="w-full px-2 py-1 border rounded text-sm bg-green-50 text-center">
                        {availableqty}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Qty (Pcs.)</label>
                      <input
                        type="number"
                        name="qty"
                        value={formData.qty}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                        disabled={!isFormEnabled && !isNewEntry}
                      />
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Wt. (Kg)</label>
                      <input
                        type="text"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                        disabled={!isFormEnabled && !isNewEntry}
                      />
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Remark</label>
                      <input
                        type="text"
                        name="remark"
                        value={formData.remark}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border rounded text-sm"
                        disabled={!isFormEnabled && !isNewEntry}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom action toolbar (kept like original) */}
          <div className="flex flex-wrap justify-between p-1 rounded bg-teal-700">
            <div className="flex flex-wrap gap-1">
              <button onClick={handleAddSingle} className="flex items-center gap-1 px-2 py-0.5 text-sm bg-gray-100 hover:bg-gray-200 border rounded border-gray-400">
                <AiOutlinePlus className="w-3 h-3" />
                {isNewEntry ? "Submit" : "Add"}
              </button>
              <button onClick={handleUpdateSingle} className="flex items-center gap-1 px-2 py-0.5 text-sm bg-gray-100 hover:bg-gray-200 border rounded border-gray-400" disabled={!isFormEnabled || isNewEntry}>
                <AiOutlineEdit className="w-3 h-3" />
                Update
              </button>
              <button onClick={handleDeleteSingle} className="flex items-center gap-1 px-2 py-0.5 text-sm bg-gray-100 hover:bg-gray-200 border rounded border-gray-400" disabled={!isFormEnabled || isNewEntry}>
                <AiOutlineDelete className="w-3 h-3" />
                Delete
              </button>
            </div>

            <div className="flex gap-1">
              <button onClick={() => navigate("/dispatchpdf", { state: formData })} className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-sm border rounded border-gray-400">
                <AiOutlinePrinter className="w-3 h-3" />
                Print
              </button>
              <button onClick={() => { resetForm(); fetchAllVNumbers(); }} className="flex items-center gap-1 px-2 py-0.5 text-sm bg-gray-100 hover:bg-gray-200 border rounded border-gray-400">
                <AiOutlineReload className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
